import {
  AnythingCategory,
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
} from 'constants/experience'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { unique, without } from 'lib/array/arrayUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import {
  getOptimizelyFeatureFlagVariation,
  isAllowedToTriggerExperiment,
} from 'lib/optimizely/optimizelyUtils'
import { getExperienceListKey, type ExperienceCoordinates } from 'lib/experiences/experienceUtils'
import type { ExperienceFacetFilters } from 'api/experiences'
import getObjectKey from 'lib/object/getObjectKey'

interface ExperienceSearchStreamingFilters {
  placeId?: string
  from?: string
  to?: string
  priceLte?: number
  priceGte?: number
  minRating?: number
  isGift?: boolean
  targetAudience?: Array<App.ExperienceTargetAudience>
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  bounds?: string
  leExclusive?: boolean
  coordinatesList?: Array<ExperienceCoordinates>
  timeSlots?: Array<Experiences.TimeSlotFilter>
  /** Carousels need limit > 0. Count/existence probes use countOnly instead. */
  limit?: number
  countOnly?: boolean
  streamingEnabled?: boolean
}

function isExperienceSearchStreamingFlagOn(state: App.State): boolean {
  return isAllowedToTriggerExperiment(
    true,
    state.utm.content,
    OptimizelyFeatureFlags.experiencesSearchStreaming,
  ) && getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.experiencesSearchStreaming)
}

export function isExperienceSearchPagePath(pathname: string): boolean {
  return !!matchLocaleAwarePath(pathname, { path: '/:regionCode/search/experiences', exact: true })
}

export function isExperienceSearchMapPagePath(pathname: string): boolean {
  return !!matchLocaleAwarePath(pathname, { path: '/:regionCode/search/experiences/map', exact: true })
}

export function isExperienceSearchStreamingPath(pathname: string): boolean {
  return isExperienceSearchPagePath(pathname) || isExperienceSearchMapPagePath(pathname)
}

export function isExperienceVerticalPagePath(pathname: string): boolean {
  return !!matchLocaleAwarePath(pathname, { path: '/:regionCode/experiences', exact: true })
}

export function isExperienceOfferPagePath(pathname: string): boolean {
  return !!matchLocaleAwarePath(pathname, {
    path: ['/:regionCode/experience/:slug/:id', '/:regionCode/experience/:id'],
    exact: true,
  })
}

export function isContentfulExperienceCarouselPath(pathname: string): boolean {
  return !!matchLocaleAwarePath(pathname, { path: ['/:regionCode/destination/*', '/:regionCode/campaign/*'], exact: true })
}

export function isTripPlannerPath(pathname: string): boolean {
  return !!matchLocaleAwarePath(pathname, { path: '/:regionCode/trip-planner/*', exact: true })
}

function isExperienceCarouselRoute(pathname: string): boolean {
  return isExperienceVerticalPagePath(pathname)
    || isExperienceOfferPagePath(pathname)
    || isContentfulExperienceCarouselPath(pathname)
    || isTripPlannerPath(pathname)
}

function hasUnsupportedHomepageStreamFilter(filters: ExperienceSearchStreamingFilters): boolean {
  // Pin / nearby coordinate lists are list-enriched only. Vision uses bounds and lat/lng.
  return !!filters.coordinatesList?.length
}

export function shouldUseExperienceSearchStreaming(
  state: App.State,
  filters: ExperienceSearchStreamingFilters,
): boolean {
  if (!filters.placeId || !isExperienceSearchStreamingFlagOn(state) || filters.streamingEnabled === false) return false

  const pathname = state.router.location.pathname
  if (isExperienceSearchStreamingPath(pathname)) return true

  const hasCarouselLimit = filters.limit != null && filters.limit > 0
  if (!hasCarouselLimit && !filters.countOnly) return false
  if (hasUnsupportedHomepageStreamFilter(filters)) return false
  if (!isExperienceCarouselRoute(pathname) && !filters.streamingEnabled) return false

  return true
}

/** `areas` has no Vision equivalent yet, so it stays empty on every path this gates. */
export function shouldUseExperienceFacetStreaming(state: App.State, placeId?: string): boolean {
  if (!placeId || typeof window === 'undefined') return false
  if (!isExperienceSearchStreamingFlagOn(state)) return false
  const pathname = state.router.location.pathname
  return isExperienceVerticalPagePath(pathname) || isExperienceSearchStreamingPath(pathname)
}

interface ExperienceFacetKeyFilters {
  from?: string
  to?: string
  priceGte?: number
  priceLte?: number
  leExclusive?: boolean
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  minRating?: number
  targetAudience?: Array<App.ExperienceTargetAudience>
  timeSlots?: Array<Experiences.TimeSlotFilter>
}

/** Shared so a search-page results stream and useMostPopulatedSearchExperienceFacets key facets the same way. */
export function getExperienceSearchFacetKey(
  placeId: string,
  filters: ExperienceFacetKeyFilters,
): string {
  return getObjectKey({
    placeId,
    availableFrom: filters.from,
    availableTo: filters.to,
    priceGte: filters.priceGte,
    priceLte: filters.priceLte,
    leExclusive: filters.leExclusive,
    luxPlusExclusive: filters.luxPlusFeatures?.join(','),
    ratingRange: filters.minRating != null ? String(filters.minRating) : undefined,
    targetAudience: filters.targetAudience?.join(',') || undefined,
    timeSlots: filters.timeSlots?.join(',') || undefined,
  })
}

/** Same list key as `useSmartExperiencePlace` when streaming is on. */
export function getExperienceLandingLocationCountOnlyListKey(placeId: string): string {
  return getExperienceListKey({
    placeId,
    sortBy: 'recommended',
    countOnly: true,
    searchStreaming: true,
  })
}

export function isUnfilteredExperienceLandingFacetRequest(
  filters: ExperienceFacetFilters = {},
): boolean {
  return !filters.availableFrom
    && !filters.availableTo
    && !filters.categoryValues
    && !filters.campaigns
    && !filters.collections
    && filters.priceGte == null
    && filters.priceLte == null
    && !filters.luxPlusExclusive
    && !filters.leExclusive
    && !filters.hasFreeCancellation
    && !filters.hasInstantConfirmation
    && !filters.giftingAvailable
    && !filters.targetAudience
    && !filters.ratingRange
    && !filters.timeSlots
}

export function isExperienceLandingLocationCountOnlySearch(filters: {
  countOnly?: boolean
  categoryCodes?: Array<number>
  campaigns?: Array<string>
  collections?: Array<string>
  from?: string
  to?: string
  priceLte?: number
  priceGte?: number
  minRating?: number
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  leExclusive?: boolean
  targetAudience?: Array<App.ExperienceTargetAudience>
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  isGift?: boolean
  timeSlots?: Array<Experiences.TimeSlotFilter>
  bounds?: string
  coordinatesList?: Array<ExperienceCoordinates>
}): boolean {
  if (!filters.countOnly) return false
  if (filters.categoryCodes?.length) return false
  if (filters.campaigns?.length) return false
  if (filters.collections?.length) return false
  if (filters.luxPlusFeatures?.length) return false
  if (filters.targetAudience?.length) return false
  if (filters.timeSlots?.length) return false
  if (filters.coordinatesList?.length) return false
  return !filters.from
    && !filters.to
    && filters.priceLte == null
    && filters.priceGte == null
    && filters.minRating == null
    && !filters.leExclusive
    && !filters.hasFreeCancellation
    && !filters.hasInstantConfirmation
    && !filters.isGift
    && !filters.bounds
}

export function mapSearchStreamingCategories(
  categoryCodes: Array<number>,
  categories: Array<App.ExperienceItemCategory>,
): Array<string> | undefined {
  const codes = without(
    categoryCodes,
    AnythingCategory.id,
    EXP_CATEGORY_LUX_PLUS_ID,
    EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  )
  if (!codes.length) return []

  const categoryById = new Map(categories.map(category => [Number(category.id), category]))
  const slugs: Array<string> = []

  for (const categoryId of codes) {
    const category = categoryById.get(Number(categoryId))
    if (!category?.name) return
    const slug = experienceCategoryVisionSlug(category.name)

    if (category.level === 'child' && category.parentId) {
      const parentName = categoryById.get(Number(category.parentId))?.name
      const parentSlug = parentName ? experienceCategoryVisionSlug(parentName) : undefined
      if (!parentSlug) return
      slugs.push(`${parentSlug}:${slug}`)
      continue
    }

    slugs.push(slug)
  }

  return unique(slugs)
}

/** Vision `features` CSV slugs from list-enriched boolean flags. */
export function mapSearchStreamingFeatures(filters: {
  leExclusive?: boolean
  hasFreeCancellation?: boolean
  hasInstantConfirmation?: boolean
  isGift?: boolean
}): Array<string> | undefined {
  const features: Array<string> = []
  if (filters.leExclusive) features.push('le_exclusive')
  if (filters.hasFreeCancellation) features.push('free_cancellation')
  if (filters.hasInstantConfirmation) features.push('instant_confirmation')
  if (filters.isGift) features.push('gifting_available')
  return features.length ? features : undefined
}

function splitCsv(value?: string): Array<string> | undefined {
  if (!value) return
  const parts = value.split(',').map(part => part.trim()).filter(Boolean)
  return parts.length ? parts : undefined
}

/** Map HTTP facet/list filters onto a Vision count-only stream. `undefined` means category ids could not be slugified. */
export function mapExperienceFacetFiltersToStreamParams(
  filters: ExperienceFacetFilters,
  categories: Array<App.ExperienceItemCategory>,
): {
  from?: string
  to?: string
  priceGte?: number
  priceLte?: number
  minRating?: number
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  features?: Array<string>
  targetAudience?: Array<App.ExperienceTargetAudience>
  timeSlots?: Array<Experiences.TimeSlotFilter>
  campaigns?: Array<string>
  collections?: Array<string>
  categories?: Array<string>
} | undefined {
  const categoryCodes = splitCsv(filters.categoryValues)?.map(Number).filter(Number.isFinite) ?? []
  const mappedCategories = mapSearchStreamingCategories(categoryCodes, categories)
  if (filters.categoryValues && mappedCategories === undefined) return

  return {
    from: filters.availableFrom,
    to: filters.availableTo,
    priceGte: filters.priceGte,
    priceLte: filters.priceLte,
    minRating: filters.ratingRange ? Number(filters.ratingRange) : undefined,
    luxPlusFeatures: splitCsv(filters.luxPlusExclusive) as Array<App.LuxPlusFeatureFilter> | undefined,
    features: mapSearchStreamingFeatures({
      leExclusive: filters.leExclusive,
      hasFreeCancellation: filters.hasFreeCancellation,
      hasInstantConfirmation: filters.hasInstantConfirmation,
      isGift: filters.giftingAvailable,
    }),
    targetAudience: splitCsv(filters.targetAudience) as Array<App.ExperienceTargetAudience> | undefined,
    timeSlots: splitCsv(filters.timeSlots) as Array<Experiences.TimeSlotFilter> | undefined,
    campaigns: splitCsv(filters.campaigns),
    collections: splitCsv(filters.collections),
    categories: mappedCategories,
  }
}

// Match svc-search `slugify` used when indexing experience category tags.
export function experienceCategoryVisionSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}
